import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Circle, Copy, Gift, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, errMsg, money } from "@/lib/api";
import { AudioPlayer, LyricsDisplay, ExplanationNote } from "@/components/site/Audio";
import { useAppData } from "@/context/AppData";

const Timeline = ({ t }) => (
  <ol className="space-y-4" aria-label="Order progress" data-testid="order-timeline">
    {t.steps.map((s) => (
      <li key={s.key} className="flex items-center gap-3" data-testid={`timeline-${s.key}-${s.state}`}>
        {s.state === "done" ? <Check className="h-5 w-5 text-[var(--gold-deep)]" aria-hidden /> : <Circle className={`h-5 w-5 ${s.state === "current" ? "text-[var(--rose)]" : "text-[var(--parchment)]"}`} aria-hidden />}
        <span className={s.state === "upcoming" ? "text-[var(--plum-muted)]" : ""}>{s.label}</span>
        <span className="sr-only">{s.state}</span>
      </li>
    ))}
  </ol>
);

const GiftPanel = ({ order, token, reload }) => {
  const g = order.gift;
  const [f, setF] = useState({ dedication: g?.dedication ?? order.dedication ?? "", show_lyrics: g?.show_lyrics ?? true, reveal_at: g?.reveal_at?.slice(0, 16) || "" });
  const link = g && !g.revoked ? `${window.location.origin}/gift/${g.token}` : null;
  const save = async () => {
    try {
      await api.post(`/orders/${token}/gift`, { ...f, reveal_at: f.reveal_at ? new Date(f.reveal_at).toISOString() : null });
      toast.success("Gift page saved"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card-parch p-6 sm:p-8" data-testid="gift-panel">
      <h2 className="font-display flex items-center gap-2 text-2xl"><Gift className="h-5 w-5 text-[var(--rose)]" aria-hidden />Share as a gift</h2>
      <p className="mt-2 text-sm text-[var(--plum-2)]">The gift page shows the song, title and your dedication — never your email, payment or story. Anyone with the link can open it; it isn't password-protected.</p>
      <div className="mt-5 grid grid-cols-1 gap-4 [&>*]:min-w-0">
        <label><span className="label">Dedication</span><input className="field" dir="auto" maxLength={300} value={f.dedication} onChange={(e) => setF({ ...f, dedication: e.target.value })} data-testid="gift-dedication-input" /></label>
        <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={f.show_lyrics} onChange={(e) => setF({ ...f, show_lyrics: e.target.checked })} data-testid="gift-show-lyrics" />Show lyrics on the gift page</label>
        <label><span className="label">Reveal from (optional)</span><input type="datetime-local" className="field" value={f.reveal_at} onChange={(e) => setF({ ...f, reveal_at: e.target.value })} data-testid="gift-reveal-at" />
          <span className="hint block">Controls when the link opens. It doesn't change when the song is finished.</span></label>
        <div className="flex flex-wrap gap-3">
          <button className="btn-plum" onClick={save} data-testid="gift-save-button">{link ? "Update gift page" : "Create gift link"}</button>
          {link && <button className="btn-ghost" onClick={async () => { await api.post(`/orders/${token}/gift/revoke`); toast.success("Gift link turned off"); reload(); }} data-testid="gift-revoke-button">Turn off link</button>}
        </div>
        {link && (
          <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm">
            <code className="min-w-0 flex-1 truncate" data-testid="gift-link">{link}</code>
            <button aria-label="Copy gift link" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="gift-copy-button"><Copy className="h-4 w-4" /></button>
            <a className="text-[var(--rose)] underline" href={link} target="_blank" rel="noreferrer" data-testid="gift-preview-link">Preview</a>
          </div>
        )}
      </div>
    </section>
  );
};

const RevisionPanel = ({ order, token, reload, config }) => {
  const [notes, setNotes] = useState("");
  const r = order.revision;
  return (
    <section className="card-parch p-6 sm:p-8" data-testid="revision-panel">
      <h2 className="font-display text-2xl">Request a revision</h2>
      <p className="mt-2 text-sm text-[var(--plum-2)]">{config.revision_covers}</p>
      <p className="mt-1 text-xs text-[var(--plum-muted)]">Used {r.used} of {r.allowed} · within {r.window_days} days of delivery.</p>
      {order.revisions.map((x) => <p key={x.id} className="mt-3 text-sm" data-testid="revision-status">Request from {x.created_at.slice(0, 10)}: <strong>{x.status.replace("_", " ")}</strong></p>)}
      {r.eligible && (
        <div className="mt-4 space-y-3">
          <textarea rows={3} className="field" dir="auto" maxLength={1500} placeholder="What would you like changed?" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="revision-notes" />
          <button className="btn-plum" disabled={notes.trim().length < 5} data-testid="revision-submit"
            onClick={async () => { try { await api.post(`/orders/${token}/revision`, { notes }); toast.success("Revision request sent"); setNotes(""); reload(); } catch (e) { toast.error(errMsg(e)); } }}>Send request</button>
        </div>
      )}
    </section>
  );
};

export default function Order() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { config } = useAppData();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/orders/${token}`).then((r) => setOrder(r.data)).catch((e) => setErr(errMsg(e))), [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!order?.timeline?.in_progress) return;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [order, load]);

  if (err) return <main className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="order-error"><p className="font-display text-3xl">We couldn't open this order</p><p className="mt-3 text-[var(--plum-2)]">{err}</p><Link to="/track" className="btn-ghost mt-6">Go to Order Tracking</Link></main>;
  if (!order || !config) return <div className="min-h-[60vh]" role="status" aria-label="Loading" />;
  const lang = order.song_language;
  const unpaid = ["not_started", "cancelled", "failed"].includes(order.payment_status);

  return (
    <main className="mx-auto max-w-5xl px-5 py-14 sm:px-8" data-testid="order-page">
      {params.get("new") && <p className="mb-8 rounded-xl bg-[var(--gold-soft)] p-4 text-sm" role="status" data-testid="order-new-banner">Thank you! Bookmark this page — it's your private link to the order. Reference <strong>{order.reference}</strong>.</p>}
      <p className="eyebrow">Order {order.reference}</p>
      <h1 className="font-display mt-2 text-4xl sm:text-5xl">{order.solemn ? `A song honoring ${order.recipient.name}` : `A song for ${order.recipient.name}`}</h1>
      <p className="mt-3 text-[var(--plum-2)]">{order.custom_occasion_label || order.occasion_label} · {order.style_label} · Sung in {lang.english_name} <span className="bidi-isolate" lang={lang.code}>({lang.native_name})</span></p>
      <p className="mt-1 text-sm text-[var(--plum-muted)]" data-testid="order-payment-status">Payment: {order.payment_status.replace("_", " ")}{order.price ? ` · ${money(order.price, order.currency)}` : ""}</p>

      <div className="mt-12 grid gap-10 lg:grid-cols-3">
        <aside className="lg:col-span-1">
          <Timeline t={order.timeline} />
          {order.timeline.attention && <p className="mt-6 rounded-xl bg-[var(--rose-soft)] p-4 text-sm" role="status" data-testid="order-attention">Our team is looking into something with your order and will be in touch by email.</p>}
          {order.payment_status === "pending" && <p className="mt-6 rounded-xl bg-[var(--gold-soft)] p-4 text-sm" data-testid="order-payment-pending">PayPal is still processing your payment. We'll start your song once it's confirmed.</p>}
          {unpaid && <Link to={`/create?resume=${token}`} className="btn-gold mt-6 w-full" data-testid="order-continue-button">Continue your order</Link>}
          <Link to="/contact" className="mt-6 flex items-center gap-2 text-sm text-[var(--rose)] underline" data-testid="order-support-link"><LifeBuoy className="h-4 w-4" />Get help</Link>
        </aside>
        <div className="min-w-0 space-y-8 lg:col-span-2">
          {order.song ? (
            <>
              <AudioPlayer src={`${BACKEND}${order.song.audio_path}`} title={order.song.title} subtitle={`For ${order.recipient.name}`} devSample={order.song.dev_sample}
                downloadHref={`${BACKEND}${order.song.audio_path}?download=1`} testid="delivery-audio-player" />
              <article className="card-white p-8">
                <h2 className="font-display text-2xl" lang={lang.code} dir={lang.rtl ? "rtl" : "ltr"} data-testid="delivery-song-title">{order.song.title}</h2>
                <div className="mt-5"><LyricsDisplay text={order.song.lyrics} language={lang} testid="delivery-lyrics" /></div>
              </article>
              <ExplanationNote token={token} version={order.song.version} language={lang} initial={order.song.explanation} />
              <GiftPanel order={order} token={token} reload={load} />
              <RevisionPanel order={order} token={token} reload={load} config={config} />
            </>
          ) : order.lyrics ? (
            <article className="card-parch p-8" data-testid="order-lyrics-preview">
              <p className="text-sm text-[var(--plum-muted)]">{order.lyrics_approved ? `Approved lyrics (v${order.lyrics.version})` : `Latest draft (v${order.lyrics.version}) — not yet approved`}</p>
              <h2 className="font-display mt-2 text-2xl" lang={lang.code} dir={lang.rtl ? "rtl" : "ltr"}>{order.lyrics.title}</h2>
              <div className="mt-5"><LyricsDisplay text={order.lyrics.text} language={lang} /></div>
              <div className="mt-6"><ExplanationNote token={token} version={order.lyrics.version} language={lang} initial={order.lyrics.explanation} /></div>
            </article>
          ) : (
            <p className="card-parch p-8 text-[var(--plum-2)]">Lyrics haven't been drafted yet.</p>
          )}
        </div>
      </div>
    </main>
  );
}
